
/*
 * UNBEGUN Custom Size 1.9
 *
 * - Checkbox and Shopify size selector stay synchronized.
 * - Custom-size form opens automatically.
 * - Validates brand, model and dimensions.
 * - Adds €7.50 custom-size service to cart.
 * - Supports gift wrapping and personalization.
 * - Preserves existing personalization checkout route.
 * - Localized checkout properties for NL / EN / DE / FR.
 */

(() => {
  'use strict';

  const SERVICE_VARIANT_ID = 55451478720855;
  const ROOT = '[data-unbegun-custom-size]';

  const CUSTOM_VALUES = [
    'ander type laptop',
    'other laptop type',
    'other laptop model',
    'anderer laptoptyp',
    "autre type d'ordinateur portable",
    "autre modèle d'ordinateur portable",
    'autre modele d ordinateur portable',
    'autre modèle de laptop',
    'autre modèle de portable'
  ];

  const translations = {
    nl: {
      brand: 'Vul het merk van je laptop in.',
      model: 'Vul het model van je laptop in.',
      dimensions: 'Vul de afmetingen van je laptop in.',
      format: 'Gebruik bijvoorbeeld 31,5 × 22,1 × 1,5 cm.',
      maximum: 'De maximale afmetingen zijn 35,8 × 24,81 × 3 cm.',
      variant: 'Selecteer eerst een beschikbare uitvoering.',
      service: 'De maatwerktoeslag is niet beschikbaar.',
      failed: 'Toevoegen aan winkelwagen is niet gelukt.'
    },
    en: {
      brand: 'Enter your laptop brand.',
      model: 'Enter your laptop model.',
      dimensions: 'Enter your laptop dimensions.',
      format: 'Use a format such as 31.5 × 22.1 × 1.5 cm.',
      maximum: 'The maximum dimensions are 35.8 × 24.81 × 3 cm.',
      variant: 'Please select an available option.',
      service: 'The custom-size service is unavailable.',
      failed: 'Could not add the product to your cart.'
    },
    de: {
      brand: 'Bitte gib die Marke deines Laptops ein.',
      model: 'Bitte gib das Modell deines Laptops ein.',
      dimensions: 'Bitte gib die Maße deines Laptops ein.',
      format: 'Verwende zum Beispiel 31,5 × 22,1 × 1,5 cm.',
      maximum: 'Die maximalen Maße sind 35,8 × 24,81 × 3 cm.',
      variant: 'Bitte wähle eine verfügbare Variante.',
      service: 'Der Aufpreis für Maßanfertigung ist nicht verfügbar.',
      failed: 'Das Produkt konnte nicht zum Warenkorb hinzugefügt werden.'
    },
    fr: {
      brand: 'Indiquez la marque de votre ordinateur.',
      model: 'Indiquez le modèle de votre ordinateur.',
      dimensions: 'Indiquez les dimensions de votre ordinateur.',
      format: 'Utilisez par exemple 31,5 × 22,1 × 1,5 cm.',
      maximum: 'Les dimensions maximales sont de 35,8 × 24,81 × 3 cm.',
      variant: 'Veuillez sélectionner une option disponible.',
      service: 'Le supplément sur mesure est indisponible.',
      failed: "Impossible d'ajouter le produit au panier."
    }
  };

  /*
   * Customer-facing Shopify line item properties.
   * Internal linkage properties are intentionally NOT translated.
   */

  const checkoutProperties = {
    nl: {
      custom: 'Op maat gemaakt',
      brand: 'Merk laptop',
      model: 'Model laptop',
      dimensions: 'Afmetingen laptop',
      yes: 'Ja'
    },
    en: {
      custom: 'Custom made',
      brand: 'Laptop brand',
      model: 'Laptop model',
      dimensions: 'Laptop dimensions',
      yes: 'Yes'
    },
    de: {
      custom: 'Maßanfertigung',
      brand: 'Laptopmarke',
      model: 'Laptopmodell',
      dimensions: 'Laptopmaße',
      yes: 'Ja'
    },
    fr: {
      custom: 'Sur mesure',
      brand: "Marque de l’ordinateur",
      model: "Modèle de l’ordinateur",
      dimensions: "Dimensions de l’ordinateur",
      yes: 'Oui'
    }
  };

  function language() {
    const lang = (document.documentElement.lang || 'nl')
      .slice(0, 2)
      .toLowerCase();

    return translations[lang] ? lang : 'nl';
  }

  function message(key) {
    return translations[language()][key];
  }

  function checkoutLabels() {
    return checkoutProperties[language()];
  }

  function normalize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[’‘]/g, "'")
      .replace(/\s+/g, ' ');
  }

  function isCustomValue(value) {
    return CUSTOM_VALUES.some(item => normalize(item) === normalize(value));
  }

  function shopRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function createLink() {
    return (
      `UC-${Date.now()}-` +
      Math.random().toString(36).slice(2, 8).toUpperCase()
    );
  }

  function getModule(form) {
    return form?.querySelector(ROOT) || null;
  }

  function getSizeSelect(form) {
    if (!form) return null;

    return Array.from(
      form.querySelectorAll('select[name^="options["]')
    ).find(select => {
      const name = (
        select.name.match(/^options\[(.*)\]$/)?.[1] || ''
      ).toLowerCase();

      return [
        'size',
        'maat',
        'größe',
        'groesse',
        'taille'
      ].includes(name);
    }) || null;
  }

  function isCustomSelected(form) {
    const select = getSizeSelect(form);

    return Boolean(
      select && isCustomValue(select.value)
    );
  }

  function findCustomOption(select) {
    return Array.from(select?.options || []).find(option =>
      isCustomValue(option.value) ||
      isCustomValue(option.textContent)
    ) || null;
  }

  function getSelection(form) {
    const module = getModule(form);

    if (!module || !isCustomSelected(form)) {
      return { enabled: false };
    }

    return {
      enabled: true,
      variantId: SERVICE_VARIANT_ID,
      brand: module.querySelector(
        '[data-custom-size-brand]'
      )?.value.trim() || '',
      model: module.querySelector(
        '[data-custom-size-model]'
      )?.value.trim() || '',
      dimensions: module.querySelector(
        '[data-custom-size-dimensions]'
      )?.value.trim() || '',
      module
    };
  }

  function parseDimensions(value) {
    const cleaned = String(value || '')
      .trim()
      .replace(/\s*cm\s*$/i, '');

    const parts = cleaned.split(/\s*[×xX*]\s*/);

    if (parts.length !== 3) return null;

    const pattern = /^\d+(?:[.,]\d+)?$/;

    if (!parts.every(part => pattern.test(part.trim()))) {
      return null;
    }

    const numbers = parts.map(part =>
      Number(part.trim().replace(',', '.'))
    );

    if (numbers.some(n => !Number.isFinite(n) || n <= 0)) {
      return null;
    }

    return numbers;
  }

  function getFields(module) {
    return {
      brand: module.querySelector('[data-custom-size-brand]'),
      model: module.querySelector('[data-custom-size-model]'),
      dimensions: module.querySelector('[data-custom-size-dimensions]')
    };
  }

  function ensureError(input) {
    if (!input) return null;

    let error = input.parentElement.querySelector(
      '[data-custom-size-error]'
    );

    if (!error) {
      error = document.createElement('p');
      error.setAttribute('data-custom-size-error', '');
      error.setAttribute('role', 'alert');
      error.hidden = true;

      error.style.cssText =
        'color:#c92a1d;font-size:13px;line-height:1.5;margin:7px 0 0';

      input.insertAdjacentElement('afterend', error);
    }

    if (!error.id) {
      error.id = input.id
        ? `${input.id}-error`
        : 'unbegun-custom-size-error';
    }

    return error;
  }

  function setError(input, text = '') {
    if (!input) return;

    const error = ensureError(input);
    const invalid = Boolean(text);

    error.textContent = text;
    error.hidden = !invalid;

    input.classList.toggle('is-error', invalid);
    input.setAttribute('aria-invalid', String(invalid));

    if (invalid) {
      input.setAttribute('aria-describedby', error.id);
      input.style.borderColor = '#c92a1d';
    } else {
      if (input.getAttribute('aria-describedby') === error.id) {
        input.removeAttribute('aria-describedby');
      }

      input.style.removeProperty('border-color');
    }
  }

  function checkField(name, value) {
    if (name === 'brand') {
      return value ? '' : message('brand');
    }

    if (name === 'model') {
      return value ? '' : message('model');
    }

    if (name === 'dimensions') {
      if (!value) return message('dimensions');

      const dimensions = parseDimensions(value);

      if (!dimensions) return message('format');

      if (
        dimensions[0] > 35.8 ||
        dimensions[1] > 24.81 ||
        dimensions[2] > 3
      ) {
        return message('maximum');
      }
    }

    return '';
  }

  function clearErrors(module) {
    if (!module) return;

    Object.values(getFields(module)).forEach(input => {
      setError(input);
    });
  }

  function validate(form, options = {}) {
    const selection = getSelection(form);

    if (!selection.enabled) return true;

    const fields = getFields(selection.module);
    let firstInvalid = null;

    ['brand', 'model', 'dimensions'].forEach(name => {
      const input = fields[name];
      const error = checkField(name, selection[name]);

      setError(input, error);

      if (error && !firstInvalid) {
        firstInvalid = input;
      }
    });

    if (!firstInvalid) return true;

    if (options.focus !== false) {
      firstInvalid.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      firstInvalid.focus({ preventScroll: true });
    }

    return false;
  }

  function appendToPayload(payload, form, quantity, link) {
    const selection = getSelection(form);

    if (!selection.enabled) return payload;

    if (!validate(form, { focus: true })) {
      return null;
    }

    if (!payload?.items?.length) return null;

    const base = payload.items[0];
    const labels = checkoutLabels();

    base.properties = Object.assign(
      {},
      base.properties || {},
      {
        [labels.custom]: labels.yes,
        [labels.brand]: selection.brand,
        [labels.model]: selection.model,
        [labels.dimensions]: selection.dimensions,

        '_Maatwerk koppeling': link,
        '_Maatwerk variant': String(SERVICE_VARIANT_ID)
      }
    );

    payload.items.push({
      id: SERVICE_VARIANT_ID,
      quantity,
      properties: {
        '_Unbegun service': 'maatwerk',
        '_Maatwerk koppeling': link
      }
    });

    return payload;
  }

  /*
   * This route handles custom-size orders WITHOUT initials.
   *
   * Orders WITH initials remain handled by the existing
   * Unbegun Personalizer, which already calls appendToPayload.
   */

  const busyForms = new WeakSet();

  async function addCustomProduct(form) {
    if (busyForms.has(form)) return;

    if (!validate(form, { focus: true })) return;

    if (
      window.UnbegunIpadKeyboard?.validate &&
      !window.UnbegunIpadKeyboard.validate(form, { focus: true })
    ) {
      return;
    }

    const baseVariantId = Number(
      form.querySelector('[name="id"]')?.value || 0
    );

    const quantity = Math.max(
      1,
      Number(form.querySelector('[name="quantity"]')?.value || 1)
    );

    const button = form.querySelector('[data-add-button]');

    if (!baseVariantId) {
      window.alert(message('variant'));
      return;
    }

    const payload = {
      items: [{
        id: baseVariantId,
        quantity,
        properties: {}
      }]
    };

    const customLink = createLink();

    if (!appendToPayload(payload, form, quantity, customLink)) {
      return;
    }

    const gift = window.UnbegunGiftWrap?.getSelection?.(form);

    if (gift?.enabled) {
      if (!gift.variantId) {
        window.alert(message('failed'));
        return;
      }

      const giftLink = createLink().replace('UC-', 'UG-');

      window.UnbegunGiftWrap.appendToPayload(
        payload,
        form,
        quantity,
        giftLink
      );
    }

    const keyboard =
      window.UnbegunIpadKeyboard?.getSelection?.(form);

    if (keyboard?.enabled) {
      const keyboardLink = createLink().replace('UC-', 'UK-');

      window.UnbegunIpadKeyboard.appendToPayload(
        payload,
        form,
        quantity,
        keyboardLink
      );
    }

    window.UnbegunPreorder?.appendToPayload?.(
      payload,
      form
    );

    busyForms.add(form);
    button?.setAttribute('disabled', 'disabled');

    try {
      const response = await fetch(
        `${shopRoot()}cart/add.js`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        }
      );

      const result = await response.json();

      if (!response.ok || result.status === 422) {
        throw new Error(
          result.description || message('failed')
        );
      }

      if (window.UnbegunCart?.openAfterMutation) {
        await window.UnbegunCart.openAfterMutation();
      } else if (window.UnbegunPersonalizer?.refresh) {
        const refreshed =
          await window.UnbegunPersonalizer.refresh();

        const toggle = document.querySelector(
          '.js-cart-drawer-toggle'
        );

        if (refreshed && toggle) {
          toggle.click();
        } else {
          window.location.assign(`${shopRoot()}cart`);
        }
      } else {
        window.location.assign(`${shopRoot()}cart`);
      }
    } catch (error) {
      console.error('[UNBEGUN Custom Size]', error);
      window.alert(error.message || message('failed'));
    } finally {
      busyForms.delete(form);
      button?.removeAttribute('disabled');
    }
  }

  /*
   * Capture the custom-only and custom + gift-wrap routes
   * before the theme's normal add-to-cart handlers.
   */

  function interceptOrder(event) {
    let form = null;

    if (event.type === 'submit') {
      form = event.target.closest?.(
        'form[data-product-form]'
      );
    } else {
      const button = event.target.closest?.(
        '[data-add-button]'
      );

      form = button?.closest('form[data-product-form]');
    }

    if (!form || !getSelection(form).enabled) return;

    const personalizerEnabled = Boolean(
      form.querySelector(
        '[data-personalizer-toggle]'
      )?.checked
    );

    if (personalizerEnabled) {
      /*
       * The existing personalizer handles this order.
       * Its payload already supports custom sizing.
       */
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (busyForms.has(form)) return;

    addCustomProduct(form);
  }

  function init(module) {
    if (module.dataset.customSizeInitialized === 'true') {
      return;
    }

    const form = module.closest('form[data-product-form]');
    const checkbox = module.querySelector(
      '[data-custom-size-toggle]'
    );
    const content = module.querySelector(
      '[data-custom-size-content]'
    );
    const sizeSelect = getSizeSelect(form);

    if (!form || !checkbox || !content || !sizeSelect) {
      return;
    }

    module.dataset.customSizeInitialized = 'true';

    const toggleRow = checkbox.closest(
      '.unbegun-custom-size__toggle-row'
    );

    if (toggleRow) {
      toggleRow.hidden = false;
      toggleRow.style.removeProperty('display');
    }

    let syncing = false;

    function updateState() {
      const active = isCustomSelected(form);
      const changed = checkbox.checked !== active;

      checkbox.checked = active;
      content.hidden = !active;

      module.classList.toggle('is-open', active);
      checkbox.setAttribute(
        'aria-expanded',
        String(active)
      );

      if (!active) {
        clearErrors(module);
      }

      if (changed) {
        checkbox.dispatchEvent(
          new Event('change', { bubbles: true })
        );
      }

      window.UnbegunGiftWrap?.updateSummary?.(form);
    }

    sizeSelect.addEventListener('change', () => {
      if (!syncing) updateState();
    });

    checkbox.addEventListener('change', () => {
      if (syncing) return;

      syncing = true;

      if (checkbox.checked) {
        const customOption = findCustomOption(sizeSelect);

        if (customOption) {
          sizeSelect.value = customOption.value;
          sizeSelect.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        }
      } else {
        const standardOption = Array.from(
          sizeSelect.options
        ).find(option =>
          !option.disabled &&
          !isCustomValue(option.value)
        );

        if (standardOption) {
          sizeSelect.value = standardOption.value;
          sizeSelect.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        }
      }

      syncing = false;
      updateState();
    });

    form.addEventListener('change', event => {
      if (event.target !== checkbox) {
        updateState();
      }
    });

    Object.entries(getFields(module)).forEach(
      ([name, input]) => {
        if (!input) return;

        ensureError(input);

        input.addEventListener('input', () => {
          const error = input.parentElement.querySelector(
            '[data-custom-size-error]'
          );

          if (error && !error.hidden) {
            setError(
              input,
              checkField(name, input.value.trim())
            );
          }
        });

        input.addEventListener('blur', () => {
          if (isCustomSelected(form) && input.value.trim()) {
            setError(
              input,
              checkField(name, input.value.trim())
            );
          }
        });
      }
    );

    updateState();
  }

  function initAll(root = document) {
    if (root.matches?.(ROOT)) {
      init(root);
    }

    root.querySelectorAll?.(ROOT).forEach(init);
  }

  window.UnbegunCustomSize = {
    getSelection,
    validate,
    appendToPayload,
    variantId: SERVICE_VARIANT_ID
  };

  document.addEventListener(
    'click',
    interceptOrder,
    true
  );

  document.addEventListener(
    'submit',
    interceptOrder,
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAll()
    );
  } else {
    initAll();
  }

  document.addEventListener(
    'shopify:section:load',
    event => initAll(event.target)
  );
})();